import React, { useEffect, useMemo, useState } from "react";
import TextField from "@mui/material/TextField";
import {
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  Select,
  MenuItem,
} from "@mui/material";
import Pagination from "@mui/material/Pagination";
import { ImFilePdf, ImImage, ImFileWord, ImFileExcel } from "react-icons/im";
import axios from "axios";
import { FadeLoader } from "react-spinners";
import { API_BASE_URL } from "../../config/api";

const OpstiAkti = () => {
  const [data, setData] = useState([]);
  const [filter, setFilter] = useState([]);
  const [loading, setLoading] = useState(true);

  const [currentPage, setCurrentPage] = useState(1);
  const recordsPerPage = 10;

  const [selectedYear, setSelectedYear] = useState("Sve");
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    async function fetchData() {
      try {
        let allPosts = [];
        let totalPages = 1;
        let page = 1;

        while (page <= totalPages) {
          const response = await axios.get(
            `${API_BASE_URL}/posts&categories=6&_embed&_fields=id,title,date,acf&acf_format=standard&per_page=${recordsPerPage}&page=${page}`
          );

          allPosts = allPosts.concat(response.data);
          totalPages = Number(response.headers["x-wp-totalpages"] || 1);
          page++;
        }

        // Najnoviji prvi
        allPosts.sort((a, b) => new Date(b.date) - new Date(a.date));

        setData(allPosts);
        setFilter(allPosts);
      } catch (err) {
        console.error("API error:", err);
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, []);

  const availableYears = useMemo(() => {
    const years = data
      .map((post) => (post?.date ? new Date(post.date).getFullYear() : null))
      .filter(Boolean)
      .map(String);
    return [...new Set(years)].sort((a, b) => Number(b) - Number(a));
  }, [data]);

  const applyFilters = (baseData, year, query) => {
    let out = baseData;

    // filter po godini
    if (year !== "Sve") {
      out = out.filter((post) => {
        const y = post?.date ? String(new Date(post.date).getFullYear()) : "";
        return y === year;
      });
    }

    // search po naslovu
    const q = (query ?? "").toLowerCase().trim();
    if (q) {
      out = out.filter((post) =>
        (post?.title?.rendered ?? "").toLowerCase().includes(q)
      );
    }

    return out;
  };

  const handleSearchChange = (val) => {
    setSearchQuery(val);
    const next = applyFilters(data, selectedYear, val);
    setFilter(next);
    setCurrentPage(1);
  };

  const handleYearFilterChange = (event) => {
    const year = event.target.value;
    setSelectedYear(year);
    const next = applyFilters(data, year, searchQuery);
    setFilter(next);
    setCurrentPage(1);
  };

  const nPages = Math.ceil(filter.length / recordsPerPage);

  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = filter.slice(indexOfFirstRecord, indexOfLastRecord);

  return (
    <div className="container drugaSekcija dekanat">
      <div className="container dekanat">
        <div className="container drugaSekcija">
          <h1>Opšti akti</h1>

          {loading ? (
            <div
              className="text-center"
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "70vh",
              }}
            >
              <FadeLoader color="#123abc" loading={loading} />
            </div>
          ) : (
            <div className="d-flex bd-highlight">
              <div className="p-2 flex-fill bd-highlight">
                <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                  <TextField
                    value={searchQuery}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    placeholder="Traži dokument"
                  />

                  <Select
                    value={selectedYear}
                    onChange={handleYearFilterChange}
                    displayEmpty
                  >
                    <MenuItem value="Sve">Sve godine</MenuItem>
                    {availableYears.map((year) => (
                      <MenuItem key={year} value={year}>
                        {year}
                      </MenuItem>
                    ))}
                  </Select>
                </div>

                <TableContainer component={Paper} style={{ marginTop: 12 }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>#</TableCell>
                        <TableCell>Naziv dokumenta</TableCell>
                        <TableCell>Datum objave</TableCell>
                        <TableCell>Preuzmi</TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {currentRecords.map((row, index) => (
                        <TableRow key={row.id}>
                          <TableCell>
                            {indexOfFirstRecord + index + 1}
                          </TableCell>

                          <TableCell>
                            {row?.title?.rendered || "—"}
                          </TableCell>

                          <TableCell>
                            {row?.date
                              ? new Date(row.date).toLocaleDateString()
                              : "—"}
                          </TableCell>

                          <TableCell>
                            {row?.acf?.dokumenti?.length ? (
                              row.acf.dokumenti.map((el) => {
                                const subtype = el?.dokument?.subtype;
                                const url = el?.dokument?.url;

                                if (!url) return null;

                                let Icon = null;
                                if (subtype === "pdf") Icon = ImFilePdf;
                                else if (["jpeg", "jpg", "png"].includes(subtype))
                                  Icon = ImImage;
                                else if (["doc", "docx"].includes(subtype))
                                  Icon = ImFileWord;
                                else if (
                                  subtype ===
                                  "vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                                )
                                  Icon = ImFileExcel;

                                return (
                                  <a
                                    key={el?.naziv_dokumenta || url}
                                    href={url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ marginRight: 8 }}
                                  >
                                    {Icon ? <Icon size={22} /> : "Dokument"}
                                  </a>
                                );
                              })
                            ) : (
                              "—"
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                {nPages > 1 && (
                  <Pagination
                    count={nPages}
                    page={currentPage}
                    onChange={(_, value) => setCurrentPage(value)}
                    style={{ marginTop: 20 }}
                  />
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OpstiAkti;

// import React, { useState, useEffect } from "react";
// import { useParams } from "react-router-dom";
// import TextField from "@mui/material/TextField";
// import {
//   TableContainer,
//   Table,
//   TableHead,
//   TableBody,
//   TableRow,
//   TableCell,
//   Paper,
//   Select,
//   MenuItem,
// } from "@mui/material";
// import Pagination from "@mui/material/Pagination";
// import { ImFilePdf, ImImage, ImFileWord, ImFileExcel } from "react-icons/im";
// import axios from "axios";
// import { FadeLoader } from "react-spinners";

// const OpstiAkti = () => {
//   const { id } = useParams();
//   const [data, setData] = useState([]);
//   const [filter, setFilter] = useState([]);
//   const [loading, setLoading] = useState(true);

//   const [currentPage, setCurrentPage] = useState(1);
//   const recordsPerPage = 10;

//   const [selectedYear, setSelectedYear] = useState("Sve"); // Dodajte stanje za izabranu godinu

//   useEffect(() => {
//     async function fetchData() {
//       let allPosts = [];
//       let totalPages = 1;
//       let currentPage = 1;

//       while (currentPage <= totalPages) {
//         const response = await axios.get(
//           `https://www.asu.unsa.ba/v2/index.php/wp-json/wp/v2/posts?categories=6&_embed&?_fields=acf&acf_format=standard&per_page=${recordsPerPage}&page=${currentPage}`
//         );

//         const responseData = response.data;
//         allPosts = allPosts.concat(responseData);
//         totalPages = response.headers["x-wp-totalpages"];
//         currentPage++;
//       }

//       // Sortiranje dokumenata po datumu objave (najnoviji prvi).
//       allPosts.sort((a, b) => {
//         const dateA = new Date(a.date);
//         const dateB = new Date(b.date);
//         return dateB - dateA;
//       });

//       setData(allPosts);
//       setFilter(allPosts);
//       setLoading(false);
//     }

//     fetchData();
//   }, []);

//   const nPages = Math.ceil(filter.length / recordsPerPage);

//   const requestSearch = (searchedVal) => {
//     const filteredRows = data.filter((row) => {
//       return row.title.rendered
//         .toString()
//         .toLowerCase()
//         .includes(searchedVal.toString().toLowerCase());
//     });
//     if (searchedVal.length < 1) {
//       setFilter(data);
//     } else {
//       setFilter(filteredRows);
//     }
//   };

//   const handleChangePage = (event, newPage) => {
//     setCurrentPage(newPage);
//   };

//   const indexOfLastRecord = currentPage * recordsPerPage;
//   const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
//   const currentRecords = filter.slice(indexOfFirstRecord, indexOfLastRecord);

//   // Funkcija za filtriranje po godini
//   const handleYearFilterChange = (event) => {
//     const selectedYear = event.target.value;
//     setSelectedYear(selectedYear);

//     // Ako je izabrana opcija "Sve", vratite sve postove
//     if (selectedYear === "Sve") {
//       setFilter(data);
//     } else {
//       // Inače, filtrirajte postove po izabranoj godini
//       const filteredPosts = data.filter((post) => {
//         const postYear = new Date(post.date).getFullYear().toString();
//         return postYear === selectedYear;
//       });
//       setFilter(filteredPosts);
//     }
//   };

//   // Kreirajte listu godina iz dostupnih datuma
//   const availableYears = [...new Set(data.map((post) => new Date(post.date).getFullYear().toString()))];

//   return (
//     <div className="container drugaSekcija dekanat">
//       <div className="container dekanat">
//         <div className="container drugaSekcija">
//           <h1>Opšti akti</h1>
//           {loading ? (
//             <div
//               className="text-center"
//               style={{
//                 display: "flex",
//                 justifyContent: "center",
//                 alignItems: "center",
//                 height: "70vh",
//               }}
//             >
//               <FadeLoader color={"#123abc"} loading={loading} size={100} />
//             </div>
//           ) : (
//             <div className="d-flex bd-highlight">
//               <div className="p-2 flex-fill bd-highlight">
//                 <TextField
//                   onChange={(e) => requestSearch(e.target.value)}
//                   placeholder="Traži dokument"
//                 />
//                 {/* Dodajte dropdown listu za godine */}
//                 <Select
//                   value={selectedYear}
//                   onChange={handleYearFilterChange}
//                   displayEmpty
//                 >
//                   <MenuItem value="Sve">Sve godine</MenuItem>
//                   {availableYears.map((year) => (
//                     <MenuItem key={year} value={year}>
//                       {year}
//                     </MenuItem>
//                   ))}
//                 </Select>
//                 <TableContainer component={Paper}>
//                   <Table>
//                     <TableHead>
//                       <TableRow>
//                         <TableCell>#</TableCell>
//                         <TableCell>Naziv dokumenta</TableCell>
//                         <TableCell>Datum objave</TableCell>
//                         <TableCell>Preuzmi</TableCell>
//                       </TableRow>
//                     </TableHead>
//                     <TableBody>
//                       {currentRecords.map((row, index) => (
//                         <TableRow key={row.id}>
//                           <TableCell>{index + 1}</TableCell>
//                           <TableCell>{row.title.rendered}</TableCell>
//                           <TableCell>
//                             {new Date(row?.date).toLocaleDateString()}
//                           </TableCell>
//                           <TableCell>
//                             {row?.acf?.dokumenti ? (
//                               row?.acf?.dokumenti.map((el) => {
//                                 return (
//                                   <React.Fragment
//                                     key={el?.naziv_dokumenta}
//                                   >
//                                     {(() => {
//                                       if (el.dokument.subtype === "pdf") {
//                                         return (
//                                           <a href={el?.dokument?.url}>
//                                             <ImFilePdf size="25" />
//                                           </a>
//                                         );
//                                       } else if (
//                                         el?.dokument?.subtype === "jpeg" ||
//                                         el?.dokument?.subtype === "jpg" ||
//                                         el?.dokument?.subtype === "png"
//                                       ) {
//                                         return (
//                                           <a href={el?.dokument?.url}>
//                                             <ImImage size="25" />
//                                           </a>
//                                         );
//                                       } else if (
//                                         el?.dokument?.subtype === "docx" ||
//                                         el?.dokument?.subtype === "doc"
//                                       ) {
//                                         return (
//                                           <a href={el?.dokument?.url}>
//                                             <ImFileWord size="25" />
//                                           </a>
//                                         );
//                                       } else if (
//                                         el?.dokument?.subtype ===
//                                         "vnd.openxmlformats-officedocument.spreadsheetml.sheet"
//                                       ) {
//                                         return (
//                                           <a href={el?.dokument?.url}>
//                                             <ImFileExcel size="25" />
//                                           </a>
//                                         );
//                                       } else {
//                                         return <p>Nema dokumenta</p>;
//                                       }
//                                     })()}
//                                   </React.Fragment>
//                                 );
//                               })
//                             ) : (
//                               ""
//                             )}
//                           </TableCell>
//                         </TableRow>
//                       ))}
//                     </TableBody>
//                   </Table>
//                 </TableContainer>
//                 <Pagination
//                   count={nPages}
//                   page={currentPage}
//                   onChange={handleChangePage}
//                 />
//               </div>
//             </div>
//           )}
//         </div>
//       </div>
//     </div>
//   );
// };

// export default OpstiAkti;
